import type { ActionContext, ActionTree, Store } from "vuex";
import type { State } from "@/store/state";
import type { SiteConfigState } from "./state";
import type { SiteConfig, SiteConfigCreate } from "@/interfaces/site_config";
import { backend } from "@/api/backend";

type SiteConfigContext = ActionContext<SiteConfigState, State>;

export const actions: ActionTree<SiteConfigState, State> = {
  async actionGetSiteConfigs(context: SiteConfigContext): Promise<void> {
    try {
      const response = await backend.getSiteConfig(
        context.rootState.main.token,
      );
      if (response) {
        context.commit("setSiteConfigs", response.data);
      }

      // Check if demo mode is in site config.
      let demoMode = response.data.some(
        (config: SiteConfig) =>
          config.name === "demo_mode" && config.val === "true",
      );

      if (!demoMode) {
        try {
          const demoModeResponse = await backend.getProjectDemoMode(
            context.rootState.main.token,
          );
          demoMode = demoModeResponse.data;
        } finally {
          demoMode = false;
        }
      }

      context.commit("setDemoMode", demoMode);
    } catch (error) {
      console.log(`Getting site config failure`);
      console.log(error);
      context.commit(
        "main/addNotification",
        {
          content: "Error getting site configuration",
          color: "error",
        },
        { root: true },
      );
    }
  },
  async actionDeleteSiteConfig(
    context: SiteConfigContext,
    payload: { id: number },
  ): Promise<void> {
    try {
      const response = await backend.deleteSiteConfig(
        context.rootState.main.token,
        payload.id,
      );
      if (response) {
        await context.dispatch("actionGetSiteConfigs");
      }
    } catch (error) {
      console.log(`Remove site configuration failure`);
      console.log(error);
      context.commit(
        "main/addNotification",
        {
          content: "Failed to remove site configuration",
          color: "error",
        },
        { root: true },
      );
    }
  },
  async actionCreateSiteConfig(
    context: SiteConfigContext,
    siteConfig: SiteConfigCreate,
  ): Promise<void> {
    try {
      const response = await backend.createSiteConfig(
        context.rootState.main.token,
        siteConfig,
      );
      if (response) {
        await context.dispatch("actionGetSiteConfigs");
      }
    } catch (error) {
      console.log(`Create site config failure`);
      console.log(error);
      context.commit(
        "main/addNotification",
        {
          content: "Failed to create site config",
          color: "error",
        },
        { root: true },
      );
    }
  },
  async actionUpdateSiteConfig(
    context: SiteConfigContext,
    payload: { id: number; siteConfig: SiteConfig },
  ): Promise<void> {
    const loadingNotification = { content: "Updating", showProgress: true };
    context.commit("main/addNotification", loadingNotification, { root: true });
    try {
      const response = (
        await Promise.all([
          await backend.updateSiteConfig(
            context.rootState.main.token,
            payload.id,
            payload.siteConfig,
          ),
          await new Promise<void>((resolve) =>
            setTimeout(() => resolve(), 500),
          ),
        ])
      )[0];
      if (response) {
        await context.dispatch("actionGetSiteConfigs");
        context.commit("main/removeNotification", loadingNotification, {
          root: true,
        });
        context.commit(
          "main/addNotification",
          {
            content: `Updated ${payload.siteConfig.name}`,
            color: "success",
          },
          { root: true },
        );
      }
    } catch (error) {
      console.log(`Update site config failure`);
      console.log(error);
      context.commit("main/removeNotification", loadingNotification, {
        root: true,
      });
      context.commit(
        "main/addNotification",
        {
          content: "Failed to update site config",
          color: "error",
        },
        { root: true },
      );
    }
  },
  async getProjectTimeZone(context: SiteConfigContext): Promise<void> {
    const response = await backend.getProjectTimeZone(
      context.rootState.main.token,
    );
    context.commit("setProjectTimeZone", response.data);
  },
  async getAssuresrvVersion(context: SiteConfigContext): Promise<void> {
    try {
      const response = await backend.getHealth(context.rootState.main.token);
      context.commit("setAssuresrvVersion", response.data.version);
    } catch (error) {
      console.log(`Getting AssureSrv version failure`);
      console.log(error);
    }
  },
  async getSpectrogramSettings(context: SiteConfigContext): Promise<void> {
    try {
      const response = await backend.getSpectrogramSettings(
        context.rootState.main.token,
      );
      context.commit("setSpectrogramBaselineDays", response.data.baseline_days);
    } catch (error) {
      console.log(`Getting spectrogram settings failure`);
      console.log(error);
    }
  },
};

const NS = "siteConfig/";

export const dispatchActionGetSiteConfigs = (store: Store<State>) =>
  store.dispatch(`${NS}actionGetSiteConfigs`);
export const dispatchActionDeleteSiteConfig = (
  store: Store<State>,
  payload: { id: number },
) => store.dispatch(`${NS}actionDeleteSiteConfig`, payload);
export const dispatchActionCreateSiteConfig = (
  store: Store<State>,
  payload: SiteConfigCreate,
) => store.dispatch(`${NS}actionCreateSiteConfig`, payload);
export const dispatchActionUpdateSiteConfig = (
  store: Store<State>,
  payload: { id: number; siteConfig: SiteConfig },
) => store.dispatch(`${NS}actionUpdateSiteConfig`, payload);
export const dispatchActionGetProjectTimeZone = (store: Store<State>) =>
  store.dispatch(`${NS}getProjectTimeZone`);
export const dispatchActionGetAssuresrvVersion = (store: Store<State>) =>
  store.dispatch(`${NS}getAssuresrvVersion`);
export const dispatchActionGetSpectrogramSettings = (store: Store<State>) =>
  store.dispatch(`${NS}getSpectrogramSettings`);
